import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import Button from '../ui/Button.jsx';
import { X, ChevronRight, ChevronLeft } from 'lucide-react';

export default function GuidedTour({ steps, storageKey, onComplete, autoStart = true }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [targetRect, setTargetRect] = useState(null);
  const [visible, setVisible] = useState(false);

  // Auto-start if not already completed. Skip auto-start when the parent
  // explicitly opts out (e.g. when triggered manually via a "Start Tour"
  // button — visible is set externally then).
  useEffect(() => {
    if (!autoStart) return;
    try {
      if (localStorage.getItem(storageKey) === 'done') return;
    } catch {}
    const timer = setTimeout(() => setVisible(true), 200);
    return () => clearTimeout(timer);
  }, [storageKey, autoStart]);

  // When the step changes, drop the old rect immediately so the tooltip
  // re-renders centered while we look for the new target — otherwise it
  // hovers over the previous element until measurement succeeds, which
  // looks like the tour froze.
  useEffect(() => { setTargetRect(null); }, [currentStep]);

  // Find and measure target element. If the selector misses (responsive
  // layout hiding the sidebar, lazy-rendered page, etc.) we retry briefly,
  // then leave targetRect null so the tooltip stays in its centered fallback
  // position — never blocks navigation.
  const measureTarget = useCallback(() => {
    if (!visible || !steps[currentStep]) return;
    let attempts = 0;
    const tryMeasure = () => {
      const el = document.querySelector(steps[currentStep].selector);
      if (el) {
        const rect = el.getBoundingClientRect();
        if (rect.width > 0 && rect.height > 0) {
          setTargetRect({ top: rect.top, left: rect.left, width: rect.width, height: rect.height });
          el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          return;
        }
      }
      if (++attempts < 8) setTimeout(tryMeasure, 120);
    };
    tryMeasure();
  }, [visible, currentStep, steps]);

  useEffect(() => {
    measureTarget();
    window.addEventListener('resize', measureTarget);
    return () => window.removeEventListener('resize', measureTarget);
  }, [measureTarget]);

  const finish = () => {
    try { localStorage.setItem(storageKey, 'done'); } catch {}
    setVisible(false);
    onComplete?.();
  };

  const next = () => {
    if (currentStep < steps.length - 1) setCurrentStep((s) => s + 1);
    else finish();
  };

  const prev = () => {
    if (currentStep > 0) setCurrentStep((s) => s - 1);
  };

  if (!visible || !steps.length) return null;

  const step = steps[currentStep];
  const pad = 8;

  // Position tooltip below or above target
  let tooltipStyle = {};
  if (targetRect) {
    const below = targetRect.top + targetRect.height + pad + 20 < window.innerHeight - 200;
    tooltipStyle = {
      position: 'fixed',
      left: Math.max(16, Math.min(targetRect.left, window.innerWidth - 360)),
      top: below ? targetRect.top + targetRect.height + pad + 12 : undefined,
      bottom: below ? undefined : window.innerHeight - targetRect.top + pad + 12,
      zIndex: 10002,
    };
  } else {
    tooltipStyle = {
      position: 'fixed',
      left: '50%',
      top: '50%',
      transform: 'translate(-50%, -50%)',
      zIndex: 10002,
    };
  }

  return createPortal(
    <>
      {/* Overlay. Clicking the backdrop dismisses the tour — otherwise an
          ill-positioned tooltip off-screen would leave the user trapped. */}
      <div
        className="fixed inset-0 z-[10000]"
        style={{ background: 'rgba(0,0,0,0.55)', pointerEvents: 'auto' }}
        onClick={finish}
      />

      {/* Highlight cutout */}
      {targetRect && (
        <div
          className="fixed z-[10001] rounded-lg"
          style={{
            top: targetRect.top - pad,
            left: targetRect.left - pad,
            width: targetRect.width + pad * 2,
            height: targetRect.height + pad * 2,
            boxShadow: '0 0 0 9999px rgba(0,0,0,0.5)',
            pointerEvents: 'none',
          }}
        />
      )}

      {/* Tooltip — ALWAYS white background + dark text, regardless of app theme.
          Theme tokens (bg-popover / text-foreground / text-muted-foreground)
          swap black<->white between dark and light mode, which repeatedly
          caused invisible-tooltip bugs (dark card on dark overlay, or light
          text on white card). Hardcoding keeps this readable in every theme
          on top of the dark backdrop. */}
      <div
        style={{ ...tooltipStyle, backgroundColor: '#ffffff', color: '#111827' }}
        className="w-[340px] rounded-xl border-2 border-primary p-5 ring-4 ring-primary/30 shadow-[0_25px_50px_-12px_rgba(0,0,0,0.9)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-2">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'hsl(var(--primary))' }}>
              Step {currentStep + 1} of {steps.length}
            </p>
            <h3 className="text-sm font-bold mt-0.5" style={{ color: '#111827' }}>{step.title}</h3>
          </div>
          <button onClick={finish} className="p-1 rounded transition-colors" style={{ color: '#6b7280' }} onMouseEnter={(e) => e.currentTarget.style.color = '#111827'} onMouseLeave={(e) => e.currentTarget.style.color = '#6b7280'}>
            <X className="h-4 w-4" />
          </button>
        </div>
        <p className="text-xs leading-relaxed mb-4" style={{ color: '#4b5563' }}>{step.description}</p>
        <div className="flex items-center justify-between">
          <button onClick={finish} className="text-xs transition-colors" style={{ color: '#6b7280' }} onMouseEnter={(e) => e.currentTarget.style.color = '#111827'} onMouseLeave={(e) => e.currentTarget.style.color = '#6b7280'}>
            Skip tour
          </button>
          <div className="flex gap-2">
            {currentStep > 0 && (
              <Button variant="secondary" size="sm" onClick={prev}>
                <ChevronLeft className="h-3.5 w-3.5" /> Back
              </Button>
            )}
            <Button size="sm" onClick={next}>
              {currentStep < steps.length - 1 ? (
                <><span>Next</span><ChevronRight className="h-3.5 w-3.5" /></>
              ) : (
                'Finish'
              )}
            </Button>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
}
